import { ApiError, createApiClient, type ApiRequestOptions } from '@kent/shared';

export { ApiError };

let isRefreshing = false;

const baseClient = createApiClient('kent-auth-session');

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function attemptRefresh(): Promise<boolean> {
  if (isRefreshing) {
    return false;
  }

  isRefreshing = true;
  try {
    const stored = localStorage.getItem('kent-auth-session');
    if (!stored) {
      return false;
    }

    const parsed = JSON.parse(stored) as { state?: { refreshToken?: string } };
    const refreshToken = parsed.state?.refreshToken;
    if (!refreshToken) {
      return false;
    }

    // Call refresh endpoint directly to avoid circular dependency
    const response = await fetch('/api/auth/refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      return false;
    }

    const data = (await response.json()) as { data?: { accessToken?: string } };
    const newToken = data.data?.accessToken;
    if (!newToken) {
      return false;
    }

    // Update stored token
    const state = JSON.parse(stored) as { state: Record<string, unknown> };
    state.state.accessToken = newToken;
    localStorage.setItem('kent-auth-session', JSON.stringify(state));

    return true;
  } catch {
    return false;
  } finally {
    isRefreshing = false;
  }
}

function clearAuthAndRedirect(): void {
  const stored = localStorage.getItem('kent-auth-session');
  if (!stored) return;

  try {
    const parsed = JSON.parse(stored) as { state?: { isAuthenticated?: boolean } };
    if (parsed.state?.isAuthenticated) {
      localStorage.removeItem('kent-auth-session');
      window.location.href = '/auth';
    }
  } catch {
    // Ignore parse errors
  }
}

function wrapWithRefresh<T>(fn: () => Promise<T>, options?: ApiRequestOptions, isRetry = false): Promise<T> {
  return fn().catch(async (error: unknown) => {
    if (error instanceof ApiError && error.statusCode === 401 && !options?.skipAuth && !isRetry) {
      const refreshed = await attemptRefresh();
      if (refreshed) {
        return wrapWithRefresh(fn, options, true);
      }
      clearAuthAndRedirect();
    }
    throw error;
  });
}

function wrapWithThrottleRetry<T>(fn: () => Promise<T>, attempt = 0): Promise<T> {
  const MAX_ATTEMPTS = 3;
  const BASE_DELAY_MS = 1000;

  return fn().catch(async (error: unknown) => {
    if (error instanceof ApiError && error.statusCode === 429 && attempt < MAX_ATTEMPTS) {
      const delayMs = BASE_DELAY_MS * Math.pow(2, attempt);
      await sleep(delayMs);
      return wrapWithThrottleRetry(fn, attempt + 1);
    }
    throw error;
  });
}

export const apiClient = {
  get: <T>(endpoint: string, options?: ApiRequestOptions) =>
    wrapWithThrottleRetry(() => wrapWithRefresh(() => baseClient.get<T>(endpoint, options), options)),

  post: <T>(endpoint: string, body?: unknown, options?: ApiRequestOptions) =>
    wrapWithThrottleRetry(() => wrapWithRefresh(() => baseClient.post<T>(endpoint, body, options), options)),

  put: <T>(endpoint: string, body?: unknown, options?: ApiRequestOptions) =>
    wrapWithThrottleRetry(() => wrapWithRefresh(() => baseClient.put<T>(endpoint, body, options), options)),

  delete: <T>(endpoint: string, options?: ApiRequestOptions) =>
    wrapWithThrottleRetry(() => wrapWithRefresh(() => baseClient.delete<T>(endpoint, options), options)),
};
